/**
 * KRA Photo Journal – FilterBar
 *
 * Contains:
 *   - Search input (by name, code, or date)
 *   - Photographer filter pills
 *   - Sort controls: field (Ngày trả ảnh / Ngày chụp) + direction (Mới nhất / Cũ nhất)
 *   - Quick filter chips: All / Liked / Not downloaded / Downloaded
 */

import React, { useRef, useState, useMemo } from "react";
import { motion } from "framer-motion";
import type { SortField, SortDir } from "../hooks/usePhotos.js";
import type { QuickFilter } from "../App.js";
import type { PhotoRecord } from "@kra/photo-journal-shared";
import { cn } from "../lib/utils.js";
import { TagFilterPanel } from "./TagFilterPanel.js";
import { FolderFilterPanel } from "./FolderFilterPanel.js";
import type { TagOptions, DriveFolderCount } from "../lib/api.js";
import { type TagFilters, countActiveTagFilters } from "../lib/tagHelpers.js";

/** Short display names for photographer pills */
const PHOTOGRAPHER_DISPLAY: Record<string, string> = {
  "Duy Phú Quý": "Quý",
  "Đức Phú": "Phú",
  "Long Cao": "Long",
};

interface FilterBarProps {
  photographers: string[];
  selectedPhotographer: string | null;
  onSelectPhotographer: (name: string | null) => void;
  sortField: SortField;
  sortDir: SortDir;
  onSortFieldChange: (f: SortField) => void;
  onSortDirChange: (d: SortDir) => void;
  totalCount: number;
  search: string;
  onSearch: (q: string) => void;
  quickFilter: QuickFilter;
  onQuickFilter: (f: QuickFilter) => void;
  /** Tag filters state */
  tagFilters: TagFilters;
  onTagFilters: (f: TagFilters) => void;
  /** Month filter: "YYYY-MM" or null */
  monthFilter: string | null;
  onMonthFilter: (m: string | null) => void;
  /** Distinct Drive category folders across the whole table (server-side) */
  driveFolders?: DriveFolderCount[];
  /** Active Drive-folder filter, or null */
  categoryFilter?: string | null;
  onCategoryFilter?: (c: string | null) => void;
  /** All loaded photos for deriving tag options in panel */
  allPhotos: PhotoRecord[];
  /** Full Airtable multi-select option list (so the panel can show every
   *  defined tag, not just ones present on loaded photos). */
  tagOptions?: TagOptions;
  /** Kiosk / read-only mode — hide liked/downloaded filters */
  isReadOnly?: boolean;
  /** First page still in flight — distinguishes "loading" from "0 results" */
  isLoading?: boolean;
}

export function FilterBar({
  photographers,
  selectedPhotographer,
  onSelectPhotographer,
  sortField,
  sortDir,
  onSortFieldChange,
  onSortDirChange,
  totalCount,
  search,
  onSearch,
  quickFilter,
  onQuickFilter,
  tagFilters,
  onTagFilters,
  monthFilter,
  onMonthFilter,
  driveFolders,
  categoryFilter,
  onCategoryFilter,
  allPhotos,
  tagOptions,
  isReadOnly,
  isLoading,
}: FilterBarProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [tagPanelOpen, setTagPanelOpen] = useState(false);
  const [folderPanelOpen, setFolderPanelOpen] = useState(false);
  const activeTagCount = countActiveTagFilters(tagFilters);

  // Derive available months from loaded photos (shootDate), newest first
  const availableMonths = useMemo(() => {
    const set = new Set<string>();
    for (const p of allPhotos) {
      if (p.shootDate) set.add(p.shootDate.slice(0, 7));
    }
    return Array.from(set).sort().reverse();
  }, [allPhotos]);

  return (
    <>
    <div className="sticky top-0 z-20 bg-surface-0/90 backdrop-blur-sm border-b border-surface-2">
      <div className="max-w-screen-xl mx-auto px-4 pt-3 pb-2 flex flex-col gap-2">

        {/* ── Search + Sort row ──────────────────────────────────────── */}
        <div className="flex flex-wrap items-center gap-2">
          {/* Search input */}
          <div className="relative flex-1 min-w-[140px]">
            <svg
              className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-ink-3 pointer-events-none"
              fill="none" stroke="currentColor" strokeWidth={2}
              viewBox="0 0 24 24"
            >
              <circle cx="11" cy="11" r="8" />
              <path d="m21 21-4.35-4.35" />
            </svg>
            <input
              ref={inputRef}
              type="text"
              value={search}
              onChange={(e) => onSearch(e.target.value)}
              placeholder="Tìm tên, mã, ngày…"
              className={cn(
                "w-full pl-8 pr-8 py-1.5 rounded-full text-xs",
                "bg-surface-1 border border-surface-2",
                "text-ink-0 placeholder:text-ink-3",
                "focus:outline-none focus:border-ink-3 focus:bg-surface-0",
                "transition-colors duration-150"
              )}
            />
            {search && (
              <button
                onClick={() => { onSearch(""); inputRef.current?.focus(); }}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-ink-3 hover:text-ink-1"
                aria-label="Clear search"
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                  <path d="M18 6 6 18M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>

          {/* Sort field: Ngày trả ảnh | Ngày chụp */}
          <div className="flex items-center gap-1 bg-surface-2 rounded-full p-0.5 flex-shrink-0">
            <SortButton active={sortField === "returnDate"} onClick={() => onSortFieldChange("returnDate")}>
              Ngày trả
            </SortButton>
            <SortButton active={sortField === "shootDate"} onClick={() => onSortFieldChange("shootDate")}>
              Ngày chụp
            </SortButton>
          </div>

          {/* Sort direction: Mới nhất | Cũ nhất */}
          <div className="flex items-center gap-1 bg-surface-2 rounded-full p-0.5 flex-shrink-0">
            <SortButton active={sortDir === "desc"} onClick={() => onSortDirChange("desc")}>
              <span className="flex items-center gap-0.5">
                <ArrowDownIcon />
                Mới
              </span>
            </SortButton>
            <SortButton active={sortDir === "asc"} onClick={() => onSortDirChange("asc")}>
              <span className="flex items-center gap-0.5">
                <ArrowUpIcon />
                Cũ
              </span>
            </SortButton>
          </div>

          {/* Tag filter button */}
          <button
            onClick={() => setTagPanelOpen(true)}
            className={cn(
              "flex-shrink-0 flex items-center gap-1 px-2.5 py-1.5 rounded-full text-xs font-medium",
              "transition-colors duration-150",
              activeTagCount > 0
                ? "bg-ink-0 text-surface-0"
                : "bg-surface-2 text-ink-2 hover:bg-surface-3 hover:text-ink-1"
            )}
            title="Lọc theo tag"
          >
            <TagIcon />
            {activeTagCount > 0 ? `Tags (${activeTagCount})` : "Tags"}
          </button>

          {/* Drive folder filter button — ~36 folders, so a panel, not pills */}
          {driveFolders && driveFolders.length > 0 && onCategoryFilter && (
            <button
              onClick={() => setFolderPanelOpen(true)}
              className={cn(
                "flex-shrink-0 flex items-center gap-1 px-2.5 py-1.5 rounded-full text-xs font-medium",
                "max-w-[180px] transition-colors duration-150",
                categoryFilter
                  ? "bg-ink-0 text-surface-0"
                  : "bg-surface-2 text-ink-2 hover:bg-surface-3 hover:text-ink-1"
              )}
              title="Lọc theo folder trên Drive"
            >
              <span>📁</span>
              <span className="truncate">{categoryFilter ?? "Folder"}</span>
            </button>
          )}

          {categoryFilter && onCategoryFilter && (
            <button
              onClick={() => onCategoryFilter(null)}
              className="flex-shrink-0 w-6 h-6 flex items-center justify-center rounded-full bg-surface-2 text-ink-2 hover:text-ink-0 transition-colors"
              aria-label="Bỏ lọc folder"
              title="Bỏ lọc folder"
            >
              <svg width={9} height={9} viewBox="0 0 11 11" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round">
                <path d="M1 1l9 9M10 1L1 10" />
              </svg>
            </button>
          )}
        </div>

        {/* ── Count + Quick filter + Photographer pills ──────────────── */}
        <div className="flex items-center gap-3">
          <p className="text-xs text-ink-2 flex-shrink-0">
            {isLoading ? "Loading…" : `${totalCount} records`}
          </p>

          <div className="flex gap-1.5 overflow-x-auto scrollbar-hide">
            {/* Quick filter chips */}
            <QuickFilterPill
              active={quickFilter === "all"}
              onClick={() => onQuickFilter("all")}
            >
              All
            </QuickFilterPill>
            {!isReadOnly && (
              <>
                <QuickFilterPill
                  active={quickFilter === "liked"}
                  onClick={() => onQuickFilter("liked")}
                >
                  <HeartIconSmall filled={quickFilter === "liked"} />
                  Liked
                </QuickFilterPill>
              </>
            )}

            {/* Subtle divider between quick filters and photographer pills */}
            {photographers.length > 0 && (
              <span className="flex-shrink-0 self-center w-px h-3.5 bg-surface-3 mx-0.5" aria-hidden />
            )}

            {/* Photographer pills */}
            {photographers.length > 0 && (
              <>
                <FilterPill
                  active={selectedPhotographer === null && !search}
                  onClick={() => { onSelectPhotographer(null); onSearch(""); }}
                >
                  Tất cả
                </FilterPill>
                {photographers.map((name) => (
                  <FilterPill
                    key={name}
                    active={selectedPhotographer === name}
                    onClick={() => { onSelectPhotographer(name); onSearch(""); }}
                    variant={name === "Admin" ? "admin" : undefined}
                  >
                    {PHOTOGRAPHER_DISPLAY[name] ?? name}
                  </FilterPill>
                ))}
              </>
            )}
          </div>
        </div>

        {/* ── Month filter pills ─────────────────────────────────────── */}
        {availableMonths.length > 0 && (
          <div className="flex items-center gap-2 pb-1">
            <span className="flex-shrink-0 text-xs text-ink-3">Tháng chụp</span>
            <div className="flex gap-1.5 overflow-x-auto scrollbar-hide">
              {monthFilter && (
                <QuickFilterPill active={false} onClick={() => onMonthFilter(null)}>
                  <svg className="w-2.5 h-2.5" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 12 12">
                    <path d="M2 2l8 8M10 2l-8 8" strokeLinecap="round"/>
                  </svg>
                  Xóa
                </QuickFilterPill>
              )}
              {availableMonths.map((ym) => {
                const [y, m] = ym.split("-");
                const label = `T${parseInt(m ?? "0")}/${(y ?? "0000").slice(2)}`;
                return (
                  <QuickFilterPill
                    key={ym}
                    active={monthFilter === ym}
                    onClick={() => onMonthFilter(monthFilter === ym ? null : ym)}
                  >
                    {label}
                  </QuickFilterPill>
                );
              })}
            </div>
          </div>
        )}

      </div>
    </div>

    <FolderFilterPanel
      isOpen={folderPanelOpen}
      onClose={() => setFolderPanelOpen(false)}
      folders={driveFolders ?? []}
      selected={categoryFilter ?? null}
      onSelect={(f) => onCategoryFilter?.(f)}
    />

    <TagFilterPanel
      isOpen={tagPanelOpen}
      onClose={() => setTagPanelOpen(false)}
      photos={allPhotos}
      tagOptions={tagOptions}
      filters={tagFilters}
      onFiltersChange={onTagFilters}
    />
    </>
  );
}

// ── Sub-components ─────────────────────────────────────────────────────────────

function FilterPill({
  active,
  onClick,
  children,
  variant,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
  /** "admin" adds a gold ring around the pill */
  variant?: "admin";
}) {
  return (
    <motion.button
      onClick={onClick}
      whileTap={{ scale: 0.95 }}
      className={cn(
        "flex-shrink-0 px-3 py-1 rounded-full text-xs font-medium",
        "transition-colors duration-150 cursor-pointer whitespace-nowrap",
        variant === "admin"
          ? active
            ? "bg-gradient-to-r from-[#B76E6E] to-[#8B5C5C] text-white"
            : "bg-gradient-to-r from-[#B76E6E]/15 to-[#8B5C5C]/15 text-[#B76E6E] hover:from-[#B76E6E]/25 hover:to-[#8B5C5C]/25"
          : active
            ? "bg-ink-0 text-surface-0"
            : "bg-surface-1 text-ink-1 hover:bg-surface-3"
      )}
    >
      {children}
    </motion.button>
  );
}

function QuickFilterPill({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <motion.button
      onClick={onClick}
      whileTap={{ scale: 0.95 }}
      className={cn(
        "flex-shrink-0 flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium",
        "transition-colors duration-150 cursor-pointer whitespace-nowrap",
        active
          ? "bg-ink-0 text-surface-0"
          : "bg-surface-1 text-ink-1 hover:bg-surface-3"
      )}
    >
      {children}
    </motion.button>
  );
}

function SortButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        "px-2.5 py-1 rounded-full text-xs font-medium transition-colors duration-150 whitespace-nowrap",
        active
          ? "bg-surface-0 text-ink-0 shadow-sm"
          : "text-ink-2 hover:text-ink-1"
      )}
    >
      {children}
    </button>
  );
}

function HeartIconSmall({ filled }: { filled: boolean }) {
  return (
    <svg
      width={9}
      height={9}
      viewBox="0 0 24 24"
      fill={filled ? "currentColor" : "none"}
      stroke="currentColor"
      strokeWidth={2.5}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
    </svg>
  );
}

function ArrowDownIcon() {
  return (
    <svg className="w-2.5 h-2.5" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" viewBox="0 0 12 12">
      <path d="M6 2v8M3 7l3 3 3-3" />
    </svg>
  );
}

function ArrowUpIcon() {
  return (
    <svg className="w-2.5 h-2.5" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" viewBox="0 0 12 12">
      <path d="M6 10V2M3 5l3-3 3 3" />
    </svg>
  );
}

function TagIcon() {
  return (
    <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
      <path d="M20.59 13.41l-7.17 7.17a2 2 0 01-2.83 0L2 12V2h10l8.59 8.59a2 2 0 010 2.82z"/>
      <line x1="7" y1="7" x2="7.01" y2="7"/>
    </svg>
  );
}
